import {
  FeatureLayerGeomType,
  MapDrawerEntityForPoi,
} from 'features/mapping/mapping.types';
import { useCallback } from 'react';
import { matchPath, useHistory } from 'react-router-dom';
import { atom, useRecoilState } from 'recoil';
import { defer } from 'lodash-es';
import { ROUTER_ENTITY_TO_ROUTE_PATH_MAP } from '../constants';

type MapDrawerEntityForPoiState = {
  mapDrawerEntityForPoi: MapDrawerEntityForPoi | null;
  lastSelectionTimestamp: number;
};

type UseMapDrawerEntityForPoiState = {
  mapDrawerEntityForPoi: MapDrawerEntityForPoi | null;
  setMapDrawerEntityForPoi: (
    mapDrawerEntityForPoi: MapDrawerEntityForPoi | null,
  ) => void;
};

const DEFAULT_MAP_DRAWER_ENTITY_STATE: MapDrawerEntityForPoiState = {
  mapDrawerEntityForPoi: null,
  lastSelectionTimestamp: 0,
};

export const mapDrawerEntityAtom = atom({
  key: 'DEFAULT_MAP_DRAWER_ENTITY_STATE',
  default: DEFAULT_MAP_DRAWER_ENTITY_STATE,
});

// Any POIs that are selected during this time window are considered part of the same event.
const CLICK_SAME_EVENT_WINDOW_MS = 100;

// Higher priority geometry type will get precedence when deciding which entity to select.
const CLICK_PRIORITY: Record<FeatureLayerGeomType, number> = {
  point: 3,
  line: 2,
  polygon: 1,
};

const isLocationRouterEntity = (pathname: string): boolean => {
  for (const path of Object.values(ROUTER_ENTITY_TO_ROUTE_PATH_MAP)) {
    const isMatch = matchPath(pathname, { path, exact: true });
    if (isMatch) return true;
  }
  return false;
};

const getClickPriority = (
  mapDrawerEntityForPoi: MapDrawerEntityForPoi | null,
): number => {
  if (!mapDrawerEntityForPoi) return 0;
  if ('geomType' in mapDrawerEntityForPoi) {
    return CLICK_PRIORITY[mapDrawerEntityForPoi.geomType];
  }
  // Not an external layer, give a higher priority
  return 4;
};

/**
 * Custom hook that manages map drawer entity state for POIs.
 * Other map entities' states (e.g., geoevents, alert cameras) are handled by the router.
 */
export const useMapDrawerEntityForPoiState =
  (): UseMapDrawerEntityForPoiState => {
    const [mapDrawerEntityForPoiState, setMapDrawerEntityForPoiState] =
      useRecoilState(mapDrawerEntityAtom);
    const history = useHistory();

    const setMapDrawerEntityForPoi = useCallback(
      (mapDrawerEntityForPoi: MapDrawerEntityForPoi | null) => {
        setMapDrawerEntityForPoiState((prev): MapDrawerEntityForPoiState => {
          const selectionTimestamp = Date.now();
          const locationPathname = history.location.pathname;
          const isRouterEntity = isLocationRouterEntity(locationPathname);

          // Any calls to setMapDrawerEntityForPoi within CLICK_SAME_EVENT_WINDOW_MS of the last selection
          // are considered to be part of the same event. The inverse means that if the last selection was
          // more than CLICK_SAME_EVENT_WINDOW_MS ago, the selection is considered a new event, and the new
          // POI replaces the current selection.
          if (
            !mapDrawerEntityForPoi ||
            prev.lastSelectionTimestamp + CLICK_SAME_EVENT_WINDOW_MS <
              selectionTimestamp
          ) {
            if (mapDrawerEntityForPoi && locationPathname !== '/') {
              // Defer navigation to ensure Recoil state updates complete before redirecting
              defer(() => history.push('/'));
            }

            return {
              mapDrawerEntityForPoi,
              lastSelectionTimestamp: selectionTimestamp,
            };
          }

          if (isRouterEntity) {
            return prev;
          }

          const prevClickPriority = getClickPriority(
            prev.mapDrawerEntityForPoi,
          );
          const currentClickPriority = getClickPriority(mapDrawerEntityForPoi);

          if (prevClickPriority > currentClickPriority) {
            return prev;
          }

          return {
            mapDrawerEntityForPoi,
            lastSelectionTimestamp: selectionTimestamp,
          };
        });
      },
      [history, setMapDrawerEntityForPoiState],
    );

    return {
      mapDrawerEntityForPoi: mapDrawerEntityForPoiState.mapDrawerEntityForPoi,
      setMapDrawerEntityForPoi,
    };
  };
